import type { Capability } from "@micropod/contracts";
import type { Decision } from "@micropod/domain";
import {
  AutomationBadge,
  EmptyState,
  Pane,
  PaneHeader,
  PhoneNumber,
  RelativeTime,
  ServiceWindowMeter,
  Skeleton,
} from "@micropod/ui";
import * as RovingFocus from "@radix-ui/react-roving-focus";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { LiveRegion } from "../ui/LiveRegion";
import { Composer } from "./Composer";

interface QueueRow {
  id: string;
  displayName: string | null;
  channelIdentity: string;
  preview: string | null;
  previewAt: number | null;
  windowExpiresAt: number | null;
  automationState: string;
}

interface Msg {
  id: string;
  direction: string;
  body: string | null;
  templateName: string | null;
  deliveryStatus: string | null;
  createdAt: number;
}

interface Thread {
  conversation: {
    id: string;
    channelIdentity: string;
    windowExpiresAt: number | null;
    automationState: string;
  };
  contact: { displayName: string | null; primaryPhone: string | null };
  messages: Msg[];
  capability: Capability;
  freeform: Decision;
  template: Decision;
}

type Load = "loading" | "ready" | "error";

const POLL_FOCUSED = 5_000;
const POLL_BLURRED = 30_000;
/** Within this of the bottom, the agent is "following" and we may scroll for them. */
const FOLLOW_SLACK = 80;

export function Inbox({ locale = "en-IN" }: { locale?: string }) {
  const [queue, setQueue] = useState<QueueRow[]>([]);
  const [queueState, setQueueState] = useState<Load>("loading");
  const [selected, setSelected] = useState<string | null>(null);
  const [thread, setThread] = useState<Thread | null>(null);
  const [threadState, setThreadState] = useState<Load>("loading");
  const [sending, setSending] = useState(false);
  const [optimistic, setOptimistic] = useState<Msg[]>([]);
  const [unread, setUnread] = useState(0);
  /** Held through the exit transition, or the chip reads "0 new messages" on its way out. */
  const [unreadLabel, setUnreadLabel] = useState("");
  const [announce, setAnnounce] = useState("");
  const [online, setOnline] = useState(navigator.onLine);

  const listRef = useRef<HTMLDivElement>(null);
  const msgsRef = useRef<HTMLDivElement>(null);
  /** True while the pointer or focus is inside the queue. */
  const holding = useRef(false);
  const lastCount = useRef(0);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      const next = (await api.inbox.queue.query({ limit: 50 })) as unknown as QueueRow[];
      setQueueState("ready");
      setQueue((prev) => {
        // A live re-sort must not reflow under the pointer: the row an agent is about to
        // click would move out from under them. While the queue is hovered or focused we
        // keep the order they can see and only refresh each row's contents.
        if (!holding.current || prev.length === 0) return next;
        const byId = new Map(next.map((r) => [r.id, r]));
        const held = prev.map((r) => byId.get(r.id) ?? r).filter((r) => byId.has(r.id));
        const added = next.filter((r) => !prev.some((p) => p.id === r.id));
        return [...held, ...added];
      });
    } catch {
      setQueueState("error");
    }
  }, []);

  const loadThread = useCallback(async (id: string) => {
    try {
      const t = (await api.inbox.thread.query({ conversationId: id })) as unknown as Thread | null;
      setThread(t);
      setThreadState("ready");
      setOptimistic([]);
    } catch {
      setThreadState("error");
    }
  }, []);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);
  useEffect(() => {
    if (!selected) return;
    setThreadState("loading");
    void loadThread(selected);
  }, [selected, loadThread]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    const tick = () => {
      void loadQueue();
      if (selected) void loadThread(selected);
    };
    const pace = () => {
      clearInterval(timer);
      timer = setInterval(tick, document.hasFocus() ? POLL_FOCUSED : POLL_BLURRED);
      if (document.hasFocus()) tick(); // a returning tab must not show stale rows
    };
    pace();
    window.addEventListener("focus", pace);
    window.addEventListener("blur", pace);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", pace);
      window.removeEventListener("blur", pace);
    };
  }, [selected, loadQueue, loadThread]);

  /**
   * An arriving message must never move what the agent is reading.
   *
   * If they are following the bottom, we scroll for them. If they are further up reading
   * history, the content stays exactly where it is and a count appears instead.
   */
  useEffect(() => {
    const el = msgsRef.current;
    const total = (thread?.messages.length ?? 0) + optimistic.length;
    if (!el || total === 0) return;

    const arrived = total - lastCount.current;
    lastCount.current = total;
    if (arrived <= 0) return;

    const following = el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_SLACK;
    if (following) {
      el.scrollTop = el.scrollHeight;
      setUnread(0);
    } else {
      setUnread((n) => n + arrived);
    }

    const last = thread?.messages.at(-1);
    if (last?.direction === "inbound") {
      setAnnounce(`New message from ${thread?.contact.displayName ?? "a contact"}`);
    }
  }, [thread, optimistic]);

  // The chip stays mounted so it can animate out as well as in, which means it outlives the
  // count that produced it. Holding the last non-zero label keeps it from reading
  // "0 new messages" on the way out.
  useEffect(() => {
    if (unread > 0) setUnreadLabel(`${unread} new message${unread === 1 ? "" : "s"}`);
  }, [unread]);

  /** Opening a different conversation starts its unread count fresh. */
  function selectConversation(id: string) {
    lastCount.current = 0;
    setUnread(0);
    setSelected(id);
  }

  function jumpToLatest() {
    const el = msgsRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setUnread(0);
  }

  /** j/k move the selection; Radix owns tabindex and the arrow keys. */
  function onQueueKey(e: React.KeyboardEvent) {
    if (e.key !== "j" && e.key !== "k") return;
    const items = Array.from(listRef.current?.querySelectorAll<HTMLElement>("[role=option]") ?? []);
    const at = items.indexOf(document.activeElement as HTMLElement);
    const next = items[e.key === "j" ? at + 1 : at - 1];
    if (next) {
      e.preventDefault();
      next.focus();
      next.click();
    }
  }

  async function onSend(text: string) {
    if (!selected) return;
    const temp: Msg = {
      id: `tmp_${Date.now()}`,
      direction: "outbound",
      body: text,
      templateName: null,
      deliveryStatus: "sending",
      createdAt: Date.now(),
    };
    setOptimistic((o) => [...o, temp]);
    setSending(true);
    try {
      await api.inbox.send.mutate({ conversationId: selected, text });
      await loadThread(selected);
      await loadQueue();
    } catch {
      // Mark it, never remove it — a message that silently vanishes is worse than one
      // that says it failed.
      setOptimistic((o) =>
        o.map((m) => (m.id === temp.id ? { ...m, deliveryStatus: "failed" } : m)),
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <LiveRegion message={announce} />

      <Pane
        as="nav"
        kind="queue"
        label="Conversations"
        onMouseEnter={() => {
          holding.current = true;
        }}
        onMouseLeave={() => {
          holding.current = false;
        }}
        onFocusCapture={() => {
          holding.current = true;
        }}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) holding.current = false;
        }}
      >
        <PaneHeader>
          Inbox
          <span className="counter tabular">{queue.length || ""}</span>
        </PaneHeader>

        {!online && <p className="banner">Offline — showing the last data we had.</p>}

        <div className="scroll" ref={listRef}>
          {queueState === "loading" && (
            <div className="qskel">
              <Skeleton lines={5} width="80%" />
            </div>
          )}

          {/* An outage must not render as "no conversations". That is a lie an agent acts on. */}
          {queueState === "error" && (
            <EmptyState
              title="We could not load your conversations"
              body="Nothing has been lost. This is usually a brief network problem."
              action={{ label: "Try again", onClick: () => void loadQueue() }}
            />
          )}

          {queueState === "ready" && queue.length === 0 && (
            <EmptyState
              title="No conversations yet"
              body="When someone messages your WhatsApp number, their thread appears here."
            />
          )}

          {queueState === "ready" && queue.length > 0 && (
            <RovingFocus.Root orientation="vertical" loop asChild>
              <div
                role="listbox"
                aria-label="Conversations"
                className="qlist"
                onKeyDown={onQueueKey}
              >
                {queue.map((c) => (
                  <RovingFocus.Item key={c.id} asChild focusable active={c.id === selected}>
                    <div
                      role="option"
                      tabIndex={-1}
                      aria-selected={c.id === selected}
                      className={`qrow${c.id === selected ? " on" : ""}`}
                      onClick={() => selectConversation(c.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          selectConversation(c.id);
                        }
                      }}
                    >
                      <span className="qrow-top">
                        <span className="qrow-name">
                          {c.displayName ?? <PhoneNumber value={c.channelIdentity} />}
                        </span>
                        <ServiceWindowMeter expiresAt={c.windowExpiresAt} />
                      </span>
                      <span className="qrow-bottom">
                        <span className="qrow-preview">{c.preview ?? "—"}</span>
                        {c.previewAt && <RelativeTime at={c.previewAt} locale={locale} />}
                      </span>
                    </div>
                  </RovingFocus.Item>
                ))}
              </div>
            </RovingFocus.Root>
          )}
        </div>
      </Pane>

      <Pane kind="thread">
        {!selected && (
          <>
            <PaneHeader>Thread</PaneHeader>
            <div className="scroll empty">
              <p className="empty-body">
                Choose a conversation to open it. Press <kbd>j</kbd> and <kbd>k</kbd> to move
                through the list.
              </p>
            </div>
          </>
        )}

        {selected && threadState === "error" && (
          <>
            <PaneHeader>Thread</PaneHeader>
            <div className="scroll">
              <EmptyState
                title="We could not open this conversation"
                body="Nothing has been lost — this is usually a brief network problem."
                action={{ label: "Try again", onClick: () => void loadThread(selected) }}
              />
            </div>
          </>
        )}

        {selected && thread && threadState === "ready" && (
          <>
            <PaneHeader>
              {thread.contact.displayName ?? (
                <PhoneNumber value={thread.conversation.channelIdentity} />
              )}
              <AutomationBadge state={thread.conversation.automationState} />
            </PaneHeader>

            <div className="msgs-wrap">
              <div className="scroll msgs" ref={msgsRef}>
                {[
                  ...thread.messages.map((m) => ({ m, fresh: false })),
                  ...optimistic.map((m) => ({ m, fresh: true })),
                ].map(({ m, fresh }) => (
                  <div
                    key={m.id}
                    className={`bub ${m.direction === "inbound" ? "in" : "out"}${fresh ? " sending" : ""}`}
                  >
                    {m.templateName && <span className="bub-template">{m.templateName}</span>}
                    <span className="bub-body">{m.body ?? "(no text)"}</span>
                    <span
                      className={`bub-receipt${m.deliveryStatus === "failed" ? " failed" : ""}`}
                    >
                      <RelativeTime at={m.createdAt} locale={locale} />
                      {m.direction === "outbound" && ` · ${m.deliveryStatus ?? "sent"}`}
                    </span>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className={`newmsg${unread > 0 ? "" : " out"}`}
                onClick={jumpToLatest}
              >
                {unreadLabel}
              </button>
            </div>

            <Composer
              capability={thread.capability}
              freeform={thread.freeform}
              template={thread.template}
              sending={sending}
              onSend={onSend}
            />
          </>
        )}
      </Pane>

      <Pane as="aside" kind="context" label="Contact">
        <PaneHeader>Contact</PaneHeader>
        <div className="scroll ctx">
          {thread && (
            <>
              <div className="ctx-field">
                <span className="ctx-label">Name</span>
                <span className="ctx-value">{thread.contact.displayName ?? "not given"}</span>
              </div>
              <div className="ctx-field">
                <span className="ctx-label">Number</span>
                <span className="ctx-value mono">
                  {thread.contact.primaryPhone ? (
                    <PhoneNumber value={thread.contact.primaryPhone} />
                  ) : (
                    "—"
                  )}
                </span>
              </div>
              <div className="ctx-field">
                <span className="ctx-label">Reply window</span>
                <span className="ctx-value">
                  <ServiceWindowMeter expiresAt={thread.conversation.windowExpiresAt} />
                </span>
              </div>
            </>
          )}
        </div>
      </Pane>
    </>
  );
}
